// DS EDIT 6 — Thread history performance: pure helpers (no supabase imports).

export const THREADS_PAGE_SIZE = 30;
export const RENDER_WINDOW_STEP = 30;

export interface ThreadCursor {
  updatedAt: string;
  id: string;
}

function toBase64(s: string): string {
  if (typeof btoa === "function") return btoa(unescape(encodeURIComponent(s)));
  return Buffer.from(s, "utf8").toString("base64");
}

function fromBase64(s: string): string {
  if (typeof atob === "function") return decodeURIComponent(escape(atob(s)));
  return Buffer.from(s, "base64").toString("utf8");
}

function safeIsoString(value: unknown): string {
  if (value == null || value === "") return "";
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

/** Opaque cursor — never exposes offsets or totals (lock: cursor opacity). */
export function encodeThreadCursor(cursor: ThreadCursor): string {
  return toBase64(JSON.stringify({ u: cursor.updatedAt, i: cursor.id }));
}

/** Malformed cursors decode to null → caller restarts at page 1, never a broken state. */
export function decodeThreadCursor(raw: string | null | undefined): ThreadCursor | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(fromBase64(raw)) as { u?: unknown; i?: unknown };
    if (typeof parsed.u !== "string" || typeof parsed.i !== "string") return null;
    if (!parsed.u || !parsed.i) return null;
    return { updatedAt: parsed.u, id: parsed.i };
  } catch {
    return null;
  }
}

/**
 * The +1 trick: fetch limit+1 rows, trim the extra, report hasMore and the
 * cursor of the last VISIBLE row.
 */
export function trimPageRows<T extends { id: string; updatedAt?: string | Date | null }>(
  rows: T[],
  limit: number
): { rows: T[]; hasMore: boolean; nextCursor: ThreadCursor | null } {
  const hasMore = rows.length > limit;
  const trimmed = hasMore ? rows.slice(0, limit) : rows;
  const last = trimmed[trimmed.length - 1];
  const nextCursor =
    hasMore && last
      ? {
          updatedAt: safeIsoString(last.updatedAt),
          id: String(last.id),
        }
      : null;
  return { rows: trimmed, hasMore, nextCursor: nextCursor?.updatedAt ? nextCursor : null };
}

/** Flatten pages and dedupe by id (a thread bumped between page fetches can repeat). */
export function dedupeThreadPages<T extends { id: string }>(pages: T[][]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const row of page) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  return out;
}

/** Incremental render window: grow by step, clamped to total; never below one step. */
export function growRenderWindow(current: number, total: number, step = RENDER_WINDOW_STEP): number {
  const base = Math.max(current, step);
  return Math.min(base + step, Math.max(total, step));
}

/** Exponential backoff for thread-list retries: 1s, 2s, 4s... capped at 10s. */
export function retryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 10_000);
}
